import { useTranslation } from 'react-i18next';
import { AlertTriangle, Ban, Check, Gauge, RotateCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useEvaluationTrace,
  type IRebookingEvaluationTraceStep,
} from '@/lib/disruptions/use-disruptions';

/* ── rebooking-progress-transparency (NEX-362 / M2) — the per-PNR evaluation timeline ──
 * Renders the algorithm's "innards" for ONE PNR group: each pipeline step, how many
 * candidates SURVIVED (`kept`), and the BLOCKED candidates with their REAL reason
 * (transit visa / MCT / no-inventory …). This upgrades U1's M1 placeholder into real
 * BLOCKED verdicts, keyed on the reason CODE (the substring before the first ':').
 *
 * Data comes from `useEvaluationTrace(groupId)` — a ONE-SHOT open read (realtime:
 * none, NO polling). uxChecklist: loading (skeleton) · empty (friendly, incl. a
 * degrade-open 200-empty) · error (retryable) · success (steps + BLOCKED chips).
 */

/** Trace step CODE → i18n key under `roster:evaluation.step`. The trace `step` is an
 *  open-ended code, so an unmapped one humanises instead (never surface a raw
 *  SCREAMING_CASE token to the operator). This file holds keys, never language. */
const STEP_KEY: Record<string, string> = {
  ANALYZING: 'ANALYSIS',
  ANALYSIS: 'ANALYSIS',
  URGENCY: 'URGENCY',
  MANIFEST: 'MANIFEST',
  ELIGIBILITY: 'ELIGIBILITY',
  FILTER: 'HARD_FILTERS',
  FILTERING: 'HARD_FILTERS',
  HARD_FILTER: 'HARD_FILTERS',
  HARD_FILTERS: 'HARD_FILTERS',
  SHOPPING: 'SEARCH',
  SEARCH: 'SEARCH',
  SCORING: 'SCORING',
  RANKING: 'RANKING',
  SELECTION: 'SELECTION',
  DONE: 'RESULT',
};

/** Eliminated-reason CODE prefix (before the first ':') → i18n key under
 *  `roster:evaluation.blocked`. The human detail after the colon is shown verbatim
 *  as the explanation; an unmapped code humanises so the operator stays informed. */
const BLOCKED_KEY: Record<string, string> = {
  TRANSIT_VISA: 'TRANSIT_VISA',
  VISA_TRANSIT_REQUIRED: 'TRANSIT_VISA',
  MCT_TOO_SHORT: 'MCT',
  TIGHT_MCT: 'MCT',
  NO_INVENTORY: 'NO_INVENTORY',
  NO_SEATS: 'NO_SEATS',
  LEAD_TIME: 'LEAD_TIME',
  DEPARTED: 'DEPARTED',
  EU261: 'EU261',
  BAGGAGE: 'BAGGAGE',
  CABIN_MISMATCH: 'CABIN_MISMATCH',
  INTERLINE: 'INTERLINE',
  NO_INTERLINE: 'INTERLINE',
  INTERLINE_DISABLED: 'INTERLINE',
  INTERLINE_CARRIER_NOT_ALLOWED: 'INTERLINE',
  // «Comprar en otras aerolíneas» — a flight Nexa buys (Duffel / Atlas), not interline.
  OTHER_CARRIER_PURCHASE_DISABLED: 'OTHER_CARRIER',
  OTHER_CARRIER_NOT_ALLOWED: 'OTHER_CARRIER',
  SPLIT: 'SPLIT',
  HIGH_RISK_FLIGHT: 'HIGH_RISK_FLIGHT',
  POLICY_DISABLED: 'POLICY_DISABLED',
  ONWARD_INFEASIBLE: 'ONWARD_INFEASIBLE',
};

function humanizeToken(token: string): string {
  const t = token.replace(/_/g, ' ').trim().toLowerCase();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : token;
}

/** Split a `CODE: human detail` reason string on the FIRST colon. A reason without a
 *  colon is treated as an all-code reason (no separate detail). */
export function parseBlockedReason(reason: string): { code: string; detail: string } {
  const idx = reason.indexOf(':');
  if (idx === -1) return { code: reason.trim(), detail: '' };
  return { code: reason.slice(0, idx).trim(), detail: reason.slice(idx + 1).trim() };
}

function fmt(n: number): string {
  return (Number.isFinite(n) ? n : 0).toFixed(2);
}

/** A single BLOCKED candidate chip: the code label (keyed on the reason CODE) + the
 *  human detail as the explanation. Masked candidate ids pass through untouched. */
function BlockedChip({ candidate, reason }: { candidate: string; reason: string }) {
  const { t } = useTranslation('roster');
  const { code, detail } = parseBlockedReason(reason);
  const key = BLOCKED_KEY[code.toUpperCase()];
  return (
    <li
      className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs"
      data-testid="evaluation-blocked-chip"
      data-code={code}
    >
      <span className="inline-flex shrink-0 items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
        <Ban className="h-3 w-3" aria-hidden />
        {key ? t(`evaluation.blocked.${key}`) : humanizeToken(code)}
      </span>
      <span className="font-mono text-muted-foreground">{candidate}</span>
      {detail ? <span className="text-muted-foreground">— {detail}</span> : null}
    </li>
  );
}

/** One pipeline step in the timeline: the step name, the survivor count, the
 *  step-specific signals (urgencyScore / rankingFactors) + the BLOCKED candidates. */
function TimelineStep({ step, last }: { step: IRebookingEvaluationTraceStep; last: boolean }) {
  const { t } = useTranslation('roster');
  const stepKey = STEP_KEY[step.step.toUpperCase()];
  const kept = step.kept ?? 0;
  const eliminated = step.eliminated ?? [];
  const rankingFactors = step.rankingFactors ? Object.entries(step.rankingFactors) : [];

  return (
    <li className="relative pl-6" data-testid={`evaluation-step-${step.step}`}>
      {/* Rail + node (the timeline spine). */}
      <span
        className="absolute left-[7px] top-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background"
        aria-hidden
      />
      {!last ? (
        <span className="absolute left-[11px] top-3.5 bottom-[-0.5rem] w-px bg-border" aria-hidden />
      ) : null}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm font-semibold">
          {stepKey ? t(`evaluation.step.${stepKey}`) : humanizeToken(step.step)}
        </span>
        <span
          className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400"
          data-testid="evaluation-step-kept"
        >
          <Check className="h-3 w-3" aria-hidden />
          {t('evaluation.survives', { count: kept })}
        </span>
        {eliminated.length > 0 ? (
          <span className="text-[11px] text-muted-foreground">
            {t('evaluation.blockedCount', { n: eliminated.length })}
          </span>
        ) : null}
      </div>

      {/* URGENCY step — the computed urgency score. */}
      {typeof step.urgencyScore === 'number' ? (
        <div
          className="mt-1 inline-flex items-center gap-1 font-mono text-xs text-muted-foreground"
          data-testid="evaluation-step-urgency"
        >
          <Gauge className="h-3 w-3" aria-hidden />
          {t('evaluation.urgency', { value: fmt(step.urgencyScore) })}
        </div>
      ) : null}

      {/* RANKING step — the scoring weights the engine applied. */}
      {rankingFactors.length > 0 ? (
        <div
          className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-xs text-muted-foreground"
          data-testid="evaluation-step-ranking"
        >
          <Sparkles className="h-3 w-3" aria-hidden />
          {rankingFactors.map(([k, v]) => (
            <span key={k}>
              {k}={fmt(v)}
            </span>
          ))}
        </div>
      ) : null}

      {/* BLOCKED candidates — the real "why not" per rejected candidate. */}
      {eliminated.length > 0 ? (
        <ul className="mt-1.5 space-y-1">
          {eliminated.map((e, i) => (
            <BlockedChip key={`${e.candidate}-${i}`} candidate={e.candidate} reason={e.reason} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** Three-line skeleton while the trace loads (uxChecklist: loading). */
function TimelineSkeleton() {
  return (
    <ul className="space-y-2" data-testid="evaluation-timeline-skeleton">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 shrink-0 rounded-full bg-muted" />
          <div className="h-3 w-32 animate-pulse rounded bg-muted" />
          <div className="h-3 w-16 animate-pulse rounded bg-muted" />
        </li>
      ))}
    </ul>
  );
}

/**
 * The per-PNR algorithm evaluation timeline. Mounts inside the rebooking-progress
 * dialog's PNR detail; fetches the trace once (realtime none, no polling).
 */
export function PnrEvaluationTimeline({ groupId }: { groupId: string }) {
  const { t } = useTranslation('roster');
  const { data, isLoading, isError, refetch } = useEvaluationTrace(groupId);

  // loading — nothing fetched yet.
  if (isLoading) {
    return (
      <div className="mt-1" data-testid="evaluation-timeline">
        <TimelineSkeleton />
      </div>
    );
  }

  // error — the fetch itself FAILED (network / 5xx). A degrade-open 200-empty body is
  // NOT an error (it lands in `data` with steps: []) → the empty state below.
  if (isError) {
    return (
      <div
        className="mt-1 flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-destructive"
        data-testid="evaluation-timeline-error"
      >
        <span className="flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {t('evaluation.loadError')}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          data-testid="evaluation-timeline-retry"
        >
          <RotateCw className="mr-1.5 h-3 w-3" aria-hidden />
          {t('actions.retry')}
        </Button>
      </div>
    );
  }

  const steps = data?.steps ?? [];

  // empty — degrade-open (no trace captured for this PNR). Friendly, never an error.
  if (steps.length === 0) {
    return (
      <div
        className="mt-1 rounded-md border border-dashed border-border bg-muted/20 p-3 text-center text-xs text-muted-foreground"
        data-testid="evaluation-timeline-empty"
      >
        {t('evaluation.empty')}
      </div>
    );
  }

  // success — the pipeline steps + BLOCKED verdicts.
  return (
    <ol className="mt-1 space-y-3" data-testid="evaluation-timeline">
      {steps.map((s, i) => (
        <TimelineStep key={`${s.step}-${i}`} step={s} last={i === steps.length - 1} />
      ))}
    </ol>
  );
}
